import React, { useEffect, useRef, useState } from "react";
import ReactDOM from 'react-dom';
import AuditSelect from '../../../common/AuditSelect';
import * as api from "../../../../backend/request";
import { hasUserAccess, getUserDetails } from "../../../../backend/auth";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import { Link, usePath, useHistory, navigate } from "raviger";
import Table, {
  CellTmpl,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAYLOAD,
  TitleTmpl,
  reactFormatter,
} from "../../../common/Table";
import {
  STATUS_MAPPING,
  TOOLTIP_DELAY,
  MONTHS,
} from "../../../common/Constants";
import {
  useGetAllVendorAudits,
} from "../../../../backend/query";
import { useGetVendorHeirarchyRecords } from "../../../../backend/masters";
import ModuleNotSubscribed from "../../../common/ModuleNotSubscribed";
import Icon from "../../../common/Icon";
import {
  downloadFileContent,
  preventDefault
} from "../../../../utils/common";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import AdminVendorLocations from "../../Masters/Companies/AdminVendorLocationV2";
import VendorAuditBulkDownloadModal from "./VendorAuditBulkDownloadModal";
import { useAuditReportForVendor } from "../../../../backend/exports";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { AUDIT_STATUS_LABELS, AUDIT_STATUS as VENDOR_AUDIT_STATUS } from "../../../../utils/auditStateMachine";
import DropAuditModal from "./DropAuditModal";
import './AuditCommon.css';
import './VendorAuditList.css';

// ── Constants ────────────────────────────────────────────────────────────────

const ALL_OPTION = { value: "all", label: "All" };

const STATUS_TAG_CLASSES: Record<string, string> = {
  scheduled: 'tag-scheduled',
  in_progress: 'tag-inprogress',
  preliminary_completed: 'tag-preliminary',
  final_completed: 'tag-final',
  dropped: 'tag-dropped',
};

const _currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 10 }, (_, i) => ({
  value: _currentYear - i,
  label: String(_currentYear - i),
}));

const AUDIT_STATUS_FILTER_OPTIONS = [
  {
    value: "scheduled",
    label: "Scheduled Audits",
    statuses: ["preliminary_audit_pending"],
  },
  {
    value: "in_progress",
    label: "Preliminary In Progress",
    statuses: [
      "preliminary_audit_submitted",
      "preliminary_audit_report_uploading",
      "preliminary_audit_report_failed",
      "preliminary_audit_editing",
    ],
  },
  {
    value: "preliminary_completed",
    label: "Preliminary Audit Completed",
    statuses: [
      "final_audit_pending",
      "final_audit_editing",
      "final_audit_submitted",
      "final_audit_report_uploading",
      "final_audit_report_failed",
    ],
  },
  {
    value: "final_completed",
    label: "Final Audit Completed",
    statuses: ["final_audit_published", "final_audit_report_uploaded"],
  },
  {
    value: "dropped",
    label: "Dropped Audit",
    statuses: ["dropped_audit"],
  },
];

const INCLUDE_LIST = [
  'company', 'associate_company', 'location', 'vendor_category',
  'vendor_registration', 'preliminary_report', 'final_report',
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function getPageNumbers(current: number, last: number): number[] {
  const delta = 2;
  const pages: number[] = [];
  for (let i = Math.max(1, current - delta); i <= Math.min(last, current + delta); i++) {
    pages.push(i);
  }
  return pages;
}

function getStatusPillClass(status: string): string {
  if (!status) return '';
  if (([VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING] as string[]).includes(status))
    return 'pill-scheduled';
  if (([
    VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED,
    VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_UPLOADING,
    VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_FAILED,
    VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_EDITING,
  ] as string[]).includes(status))
    return 'pill-inprogress';
  if (([
    VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED,
    VENDOR_AUDIT_STATUS.FINAL_AUDIT_PENDING,
    VENDOR_AUDIT_STATUS.FINAL_AUDIT_EDITING,
    VENDOR_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
    VENDOR_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADING,
    VENDOR_AUDIT_STATUS.FINAL_AUDIT_REPORT_FAILED,
  ] as string[]).includes(status))
    return 'pill-preliminary';
  if (([
    VENDOR_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED,
    VENDOR_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED,
  ] as string[]).includes(status))
    return 'pill-final';
  if (([VENDOR_AUDIT_STATUS.DROPPED_AUDIT] as string[]).includes(status))
    return 'pill-dropped';
  return '';
}

function TextCell({ className, value }: { className: string; value: string }) {
  const text = value || '—';
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{text}</Tooltip>}
      delay={{ show: TOOLTIP_DELAY } as any}
    >
      <td className={className}>{text}</td>
    </OverlayTrigger>
  );
}

function InfoTooltip({ html }: { html: string }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLSpanElement>(null);
  if (!html) return null;

  function handleMouseEnter() {
    if (ref && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.top, left: rect.left + rect.width / 2 });
    }
    setShow(true);
  }

  return (
    <span
      ref={ref}
      className="al-info-tooltip-wrap"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
      {show && ReactDOM.createPortal(
        <div
          className="al-info-tooltip-box"
          style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%) translateY(calc(-100% - 6px))' }}
          dangerouslySetInnerHTML={{ __html: html }}
        />,
        document.body
      )}
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

function VendorAuditList() {
  const { state }: any = useHistory();
  const path: any = usePath();
  const [fromDashboard] = useState(path.includes("/dashboard/activities"));

  const [params, setParams] = useState<any>({ page: 1, per: DEFAULT_PAGE_SIZE });
  const user = getUserDetails();
  const { companies: vendorCompanies, isFetching: checkingVendorAccess } = useGetVendorHeirarchyRecords(
    { filters: { user_id_eq: user.user_id, is_active_eq: true } }
  );
  const [data, setData] = useState<any>();
  const [locationFilters, setLocationFilter] = useState<any>(
    state && state.initialFilters ? state.initialFilters : undefined
  );
  const lfRef: any = useRef();
  lfRef.current = locationFilters;

  const [payload, setPayload] = useState<any>();
  const payloadRef = useRef<any>();
  payloadRef.current = payload;

  const [showLoader, setShowLoader] = useState(false);
  const [previousFilters, setPreviousFilters] = useState<any>(state || {});
  const previousFiltersRef = useRef<any>();
  previousFiltersRef.current = previousFilters;

  const [enableBulkDownloadModal, setEnableBulkDownloadModal] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [dropAudit, setDropAudit] = useState<any>(null);
  const [filterKey, setFilterKey] = useState(0);

  const initialAuditStatus = state && state.auditStatusFilter
    ? state.auditStatusFilter
    : (state && state.auditStatus ? { [state.auditStatus]: true } : {});
  const [auditStatusFilter, setAuditStatusFilter] = useState<any>(initialAuditStatus);
  const auditStatusRef = useRef<any>(initialAuditStatus);
  auditStatusRef.current = auditStatusFilter;

  const initialMonth = state && state.month
    ? MONTHS.find((m: any) => m.value === state.month) || ALL_OPTION
    : ALL_OPTION;
  const initialYear = state && state.year
    ? yearOptions.find((y: any) => y.value === state.year) || ALL_OPTION
    : yearOptions[0];
  const [monthFilter, setMonthFilter] = useState<any>(initialMonth);
  const [yearFilter, setYearFilter] = useState<any>(initialYear);
  const monthRef = useRef<any>(initialMonth);
  const yearRef = useRef<any>(initialYear);
  monthRef.current = monthFilter;
  yearRef.current = yearFilter;

  // ── Data hooks ──────────────────────────────────────────────────────────────

  const { activities, total, isFetching, refetch } = useGetAllVendorAudits(
    payload,
    Boolean(hasFilters(null, "company_id_eq") || hasFilters(null, "company_id_in"))
  );

  const { auditReportVendor, exporting } = useAuditReportForVendor(
    (response: any) => {
      const filename = selectedReport && selectedReport.name
        ? selectedReport.name.charAt(0).toUpperCase() + selectedReport.name.slice(1)
        : 'audit_report';
      if (response) {
        downloadFileContent({
          name: filename,
          type: response.headers["content-type"],
          content: response.data,
        });
      }
    },
    (error: any) => {
      console.log(error);
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  );

  // ── Helpers ─────────────────────────────────────────────────────────────────

  function hasFilters(ref: any, field = "company_id_eq") {
    const _filters: any =
      (ref
        ? ref.current
        : ({ ...(payloadRef.current || {}) } as any)["filters"]) || {};
    return _filters[field];
  }

  function getStatusFilters() {
    const selected = auditStatusRef.current || {};
    const selectedStatuses = AUDIT_STATUS_FILTER_OPTIONS
      .filter(opt => selected[opt.value])
      .flatMap((opt: any) => opt.statuses);
    const filters: any = selectedStatuses.length > 0
      ? { status_in: selectedStatuses }
      : { status_not_eq: VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_CREATED };

    if (monthRef.current && monthRef.current.value && monthRef.current.value !== "all") {
      filters.month_eq = monthRef.current.value;
    }
    if (yearRef.current && yearRef.current.value && yearRef.current.value !== "all") {
      filters.year_eq = String(yearRef.current.value);
    }
    return filters;
  }

  // ── Effects ─────────────────────────────────────────────────────────────────

  // Initialize payload on mount (replaces Tabulator's ajaxRequestFunc first call)
  useEffect(() => {
    const _params = { page: 1, per: DEFAULT_PAGE_SIZE, include: INCLUDE_LIST, includes: INCLUDE_LIST };
    setParams(_params);
    setPayload({
      ...DEFAULT_PAYLOAD,
      ..._params,
      filters: {
        ...(lfRef.current || {}),
        ...getStatusFilters(),
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when location / status / month / year filters change
  useEffect(() => {
    if (locationFilters) {
      setParams((prev: any) => ({ ...prev, page: 1 }));
      setPayload({
        ...DEFAULT_PAYLOAD,
        page: 1,
        per: DEFAULT_PAGE_SIZE,
        include: INCLUDE_LIST,
        includes: INCLUDE_LIST,
        filters: {
          ...locationFilters,
          ...getStatusFilters(),
        },
      });
    }
  }, [locationFilters, auditStatusFilter, monthFilter, yearFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived ──────────────────────────────────────────────────────────────────

  const hasActiveFilters =
    (monthFilter && monthFilter.value !== 'all') ||
    (yearFilter && yearOptions[0] && yearFilter.value !== yearOptions[0].value) ||
    Object.values(auditStatusFilter || {}).some(Boolean) ||
    !!(locationFilters && (
      locationFilters.company_id_eq ||
      locationFilters.associate_company_id_eq ||
      locationFilters.location_id_eq ||
      locationFilters.vendor_category_id_eq ||
      locationFilters.vendor_registration_id_eq
    ));

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function handleResetFilters() {
    setMonthFilter(ALL_OPTION);
    setYearFilter(yearOptions[0]);
    setAuditStatusFilter({});
    setLocationFilter(undefined);
    setPreviousFilters({});
    setFilterKey(k => k + 1);
    setParams({ page: 1, per: DEFAULT_PAGE_SIZE });
    setPayload({
      ...DEFAULT_PAYLOAD,
      page: 1,
      per: DEFAULT_PAGE_SIZE,
      include: INCLUDE_LIST,
      includes: INCLUDE_LIST,
      filters: {
        status_not_eq: VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_CREATED,
      },
    });
  }

  function onLocationChange(event: any, filter: any = {}) {
    setLocationFilter(event);
    if (Object.keys(filter).length > 0) {
      setPreviousFilters((prev: any) => ({ ...prev, ...filter }));
    }
  }

  async function downloadReport(audit: any) {
    if (!audit || !audit.id) return;
    setShowLoader(true);
    try {
      let reportData: any;
      let auditData: any;
      if (audit.preliminary_report || audit.final_report) {
        auditData = audit;
      } else {
        const response = await api.get(`/api/v1/vendor_location_audits/${audit.id}`, {
          include: ['preliminary_report', 'final_report'],
          includes: ['preliminary_report', 'final_report'],
        });
        auditData = response.data;
      }
      if ([
        VENDOR_AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED,
        VENDOR_AUDIT_STATUS.FINAL_AUDIT_PENDING,
        VENDOR_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
        VENDOR_AUDIT_STATUS.FINAL_AUDIT_EDITING,
      ].includes(audit.status)) {
        reportData = auditData.preliminary_report;
      } else if ([
        VENDOR_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED,
        VENDOR_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED,
      ].includes(audit.status)) {
        reportData = auditData.final_report;
      } else if (audit.status === VENDOR_AUDIT_STATUS.DROPPED_AUDIT) {
        reportData = auditData.final_report || auditData.preliminary_report;
      }
      setSelectedReport(reportData);
      if (!reportData) {
        toast.warn('No report available');
        setShowLoader(false);
        return;
      }
      const { s3_region, s3_key, s3_bucket } = reportData || {};
      if (s3_region && s3_key && s3_bucket) {
        auditReportVendor({ s3_region, s3_key, s3_bucket });
      } else {
        toast.warn('No report available');
      }
    } catch (error) {
      console.error('Error fetching audit:', error);
    }
    setShowLoader(false);
  }

  function handlePageNav(_pagination: any) {
    const _params = { ...params, ..._pagination };
    setParams({ ..._params });
    setPayload({ ...(payload as any), ..._params });
  }

  function rowClick(audit: any) {
    if (audit && audit.id) {
      navigate(`/vendor-audits/${audit.id}`, {
        state: {
          ...(previousFiltersRef.current || {}),
          initialFilters: lfRef.current,
          auditStatusFilter: auditStatusRef.current,
          month: monthRef.current && monthRef.current.value,
          year: yearRef.current && yearRef.current.value,
        },
      });
    }
  }

  function openBulkDownloadModal() {
    setEnableBulkDownloadModal(true);
  }

  const canBulkDownload =
    hasUserAccess(USER_PRIVILEGES.REVIEWER_VENDOR_ACTIVITIES_DOWNLOAD_BULK_REPORT) ||
    hasUserAccess(USER_PRIVILEGES.SUBMITTER_VENDOR_ACTIVITIES_DOWNLOAD_BULK_REPORT);

  // ── Pagination computed values ────────────────────────────────────────────

  const currentPage = (params && params.page) || 1;
  const perPage = (params && params.per) || DEFAULT_PAGE_SIZE;
  const totalRecords = total || 0;
  const lastPage = Math.ceil(totalRecords / perPage) || 1;
  const showingFrom = totalRecords > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const showingTo = Math.min(currentPage * perPage, totalRecords);

  if (checkingVendorAccess) {
    return <PageLoader />;
  }

  if (vendorCompanies.length === 0) {
    return <ModuleNotSubscribed />;
  }
  
  return (
    <>
      <div className="audit-list-page va-audit-page">

        {/* ── Header ── */}
        <div className="audit-page-header">
          <h1 className="audit-page-title">Vendor Audits</h1>
          <p className="audit-page-subtitle">All Vendor-wise compliance audits with current status and actions.</p>
        </div>

        {/* ── Filter card ── */}
        <div className="audit-filter-card">
          <div className="audit-filters-row">
            <AdminVendorLocations key={filterKey} onChange={onLocationChange} previousFilters={previousFilters} />
            <div className="audit-filter-group">
              <label><small>Month</small></label>
              <AuditSelect
                options={[ALL_OPTION, ...MONTHS]}
                onChange={(val) => setMonthFilter(val)}
                value={monthFilter}
              />
            </div>
            <div className="audit-filter-group">
              <label><small>Year</small></label>
              <AuditSelect
                options={[ALL_OPTION, ...yearOptions]}
                onChange={(val) => setYearFilter(val)}
                value={yearFilter}
              />
            </div>
            {hasActiveFilters && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Reset Filters</Tooltip>}
                delay={{ show: 300 } as any}
              >
                <button type="button" className="audit-reset-icon-btn" onClick={handleResetFilters} aria-label="Reset Filters">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="8" r="6.5" />
                    <line x1="5.5" y1="5.5" x2="10.5" y2="10.5" />
                    <line x1="10.5" y1="5.5" x2="5.5" y2="10.5" />
                  </svg>
                </button>
              </OverlayTrigger>
            )}
          </div>

          <div className="audit-status-bar">
            <span className="audit-status-label">Audit Status</span>
            {AUDIT_STATUS_FILTER_OPTIONS.map((opt) => {
              const isSelected = !!(auditStatusFilter && auditStatusFilter[opt.value]);
              const tagCls = STATUS_TAG_CLASSES[opt.value] || '';
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`audit-status-tag ${tagCls}${isSelected ? ' active-tag' : ''}`}
                  onClick={() => setAuditStatusFilter((prev: any) => ({ ...prev, [opt.value]: !prev[opt.value] }))}
                >
                  {opt.label}
                </button>
              );
            })}
            {canBulkDownload && (
              <button type="button" className="audit-bulk-btn" onClick={openBulkDownloadModal}>
                Bulk Report
              </button>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <div className="audit-table-card">
          <div className="audit-table-scroll">
            <table className="audit-table">
              <thead>
                <tr>
                  <th className="col-month">Month &amp; Year</th>
                  <th className="col-company">Company</th>
                  <th className="col-assoc">Associate Company</th>
                  <th className="col-location">Location Name</th>
                  <th className="col-vendor-cat">Vendor Category</th>
                  <th className="col-vendor">Vendor</th>
                  <th className="col-status">Status</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isFetching ? (
                  <tr className="audit-empty-row">
                    <td colSpan={8}>Loading...</td>
                  </tr>
                ) : !activities || activities.length === 0 ? (
                  <tr className="audit-empty-row">
                    <td colSpan={8}>No audits found for the selected filters.</td>
                  </tr>
                ) : (
                  (activities || []).map((audit: any) => {
                    const isDropped = audit.status === VENDOR_AUDIT_STATUS.DROPPED_AUDIT;
                    const isFinalCompleted =
                      audit.status === VENDOR_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED ||
                      audit.status === VENDOR_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED;
                    const canDropAudit = hasUserAccess(USER_PRIVILEGES.REVIEWER_VENDOR_ACTIVITIES_DROP_AUDIT);
                    const dropReason = ((audit.metadata || {}).drop_reason) as string | undefined;
                    return (
                      <tr key={audit.id} onClick={() => rowClick(audit)}>
                        <TextCell className="col-month" value={`${audit.month} (${audit.year})`} />
                        <TextCell className="col-company" value={(audit.company && audit.company.name) || '—'} />
                        <TextCell className="col-assoc" value={(audit.associate_company && audit.associate_company.name) || '—'} />
                        <TextCell className="col-location" value={(audit.location && audit.location.name) || '—'} />
                        <TextCell className="col-vendor-cat" value={(audit.vendor_category && audit.vendor_category.name) || '—'} />
                        <TextCell className="col-vendor" value={(audit.vendor_registration && audit.vendor_registration.vendor_name) || '—'} />
                        <td className="col-status">
                          <div className="d-flex align-items-center gap-1">
                            {audit.status === VENDOR_AUDIT_STATUS.DROPPED_AUDIT && (
                              <InfoTooltip html={dropReason || ""} />
                            )}
                            {audit.status && (
                              <OverlayTrigger
                                overlay={<Tooltip>{AUDIT_STATUS_LABELS[audit.status as keyof typeof AUDIT_STATUS_LABELS] || audit.status}</Tooltip>}
                                rootClose={true}
                                placement="bottom"
                                delay={{ show: TOOLTIP_DELAY } as any}
                              >
                                <span className={`audit-status-pill ${getStatusPillClass(audit.status)}`}>
                                  {AUDIT_STATUS_LABELS[audit.status as keyof typeof AUDIT_STATUS_LABELS] || audit.status}
                                </span>
                              </OverlayTrigger>
                            )}
                          </div>
                        </td>
                        <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                          <div className="audit-actions-cell">
                            <button
                              type="button"
                              className="audit-action-btn"
                              title="Download Report"
                              onClick={() => downloadReport(audit)}
                            >
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#2965ad" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            </button>
                            {canDropAudit && !isDropped && !isFinalCompleted ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id={`drop-tooltip-${audit.id}`}>Drop Audit</Tooltip>}
                              >
                                <button
                                  type="button"
                                  className="audit-action-btn"
                                  onClick={() => setDropAudit(audit)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <rect x="2.5" y="2.5" width="11" height="11" stroke="#C00F0C"/>
                                    <path d="M11 5L5 11M5 5L11 11" stroke="#C00F0C" strokeLinecap="square" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              </OverlayTrigger>
                            ) : (
                              <span style={{ width: 16, height: 16, display: 'inline-block', flexShrink: 0 }} />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          <div className="audit-pagination">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#757575', fontFamily: 'Montserrat', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  Results per page
                </span>
                <select
                  value={perPage}
                  onChange={(e: any) => handlePageNav({ page: 1, per: Number(e.target.value) })}
                  style={{ fontSize: 12, fontWeight: 600, color: '#1e1e1e', fontFamily: 'Montserrat', border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 4px', cursor: 'pointer', background: '#fff', outline: 'none' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <span className="audit-pagination-text">
                {totalRecords > 0
                  ? `Showing ${showingFrom} to ${showingTo} of ${totalRecords} results`
                  : 'No records'}
              </span>
            </div>
            <div className="audit-pagination-controls">
              <button
                className="audit-pagination-btn"
                disabled={currentPage <= 1}
                onClick={() => handlePageNav({ page: currentPage - 1, per: perPage })}
              >Previous</button>
              {getPageNumbers(currentPage, lastPage).map((n: number) => (
                <button
                  key={n}
                  className={`audit-pagination-btn${n === currentPage ? ' active' : ''}`}
                  onClick={() => handlePageNav({ page: n, per: perPage })}
                >{n}</button>
              ))}
              <button
                className="audit-pagination-btn"
                disabled={currentPage >= lastPage}
                onClick={() => handlePageNav({ page: currentPage + 1, per: perPage })}
              >Next</button>
            </div>
          </div>
        </div>
      </div>

      {enableBulkDownloadModal && (
        <VendorAuditBulkDownloadModal
          onSubmit={() => setEnableBulkDownloadModal(false)}
          onCancel={() => setEnableBulkDownloadModal(false)}
          previousFilters={previousFilters}
        />
      )}
      {dropAudit && (
        <DropAuditModal
          audit={dropAudit}
          onClose={() => setDropAudit(null)}
          onSuccess={() => {
            setDropAudit(null);
            refetch();
          }}
        />
      )}
      {(showLoader || isFetching || exporting) && <PageLoader />}
    </>
  );
}

export default VendorAuditList;
